/* Catches Convex query/mutation errors so the app shows a friendly message instead of crashing.
   Includes "Report this bug" button that opens FeedbackDialog with error context pre-filled. */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { getSessionLogs } from "@/lib/sessionLogCollector";
import { Sentry } from "@/lib/sentry";
import type { FeedbackPrefill } from "@/types/feedback";

interface ConvexQueryErrorBoundaryProps {
  children: ReactNode;
}

interface ConvexQueryErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  reportDialogOpen: boolean;
}

export class ConvexQueryErrorBoundary extends Component<
  ConvexQueryErrorBoundaryProps,
  ConvexQueryErrorBoundaryState
> {
  state: ConvexQueryErrorBoundaryState = {
    hasError: false,
    error: null,
    reportDialogOpen: false,
  };

  static getDerivedStateFromError(error: Error): Partial<ConvexQueryErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ConvexQueryErrorBoundary]", error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? undefined } },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleOpenReport = () => {
    this.setState({ reportDialogOpen: true });
  };

  handleReportOpenChange = (open: boolean) => {
    this.setState({ reportDialogOpen: open });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const isConvex =
        this.state.error.message.includes("CONVEX") ||
        this.state.error.message.includes("Not authenticated") ||
        this.state.error.message.includes("User not found");

      const prefill: FeedbackPrefill = {
        type: "bug",
        errorStack: this.state.error.stack,
        sessionLogs: getSessionLogs(),
      };

      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 px-4 text-center">
          <p className="text-muted-foreground">
            {isConvex
              ? "Something went wrong loading your data. This often happens when your session isn't fully synced yet."
              : "Something went wrong."}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button variant="outline" onClick={this.handleRetry}>
              Try again
            </Button>
            <Button variant="outline" onClick={this.handleOpenReport}>
              Report this bug
            </Button>
            <Button onClick={this.handleRefresh}>Refresh page</Button>
          </div>
          <FeedbackDialog
            open={this.state.reportDialogOpen}
            onOpenChange={this.handleReportOpenChange}
            prefill={prefill}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
